/**
 * Thankful trees — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const thankfulTreesPack: ToilePack = {
    slug: 'thankful-trees',
    category: 'season',
    name: 'Thankful trees',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'tree', label: 'Bare tree', subject: 'A vertical page with a large bare tree lettered "Our Thankful Tree" above, the branches empty for paper leaves'},
        {key: 'leaves', label: 'Blank leaves', subject: 'A sheet of paper leaves in three shapes each with a blank ruled centre to write on, dashed cut lines'},
        {key: 'jar', label: 'Gratitude jar', subject: 'A sheet with a jar label lettered "Gratitude Jar" and a set of small blank slips, dashed cut lines'},
    ],
    styles: [
        {key: 'rustic', label: 'Rustic kraft', style: 'rustic printable on kraft paper, leaf and wheat borders, warm serif lettering, print-ready', palette: 'Kraft brown, rust, mustard and cream', framing: 'Laid out on a kraft page with an even margin'},
        {key: 'elegant', label: 'Elegant', style: 'elegant printable, thin gold line motifs, refined serif lettering, print-ready', palette: 'Cream, gold and a deep burgundy', framing: 'Laid out on a cream page with wide margins'},
        {key: 'kids', label: 'Kids', style: "kids' thanksgiving printable, cartoon turkeys and leaves, rounded lettering, print-ready", palette: 'Bright orange, yellow, red and brown', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['for the whole family', 'as a daily journal page', 'with room for a photo'],
    directionHint: 'try: for the whole family',
    seo: {
        title: 'Thankful tree: tree, leaves and gratitude jar',
        metaDescription: "Nine thankful tree printables — a bare tree to fill, blank leaves to write on, a gratitude jar label — rustic, elegant or kids' style.",
        h1: 'Thankful tree: tree, leaves and gratitude jar in three styles',
        tagline: 'Nine sheets, three gratitude pieces, three styles each — a bare tree, blank leaves, a jar label.',
        intro: "A gratitude tree in three pieces: a bare tree trunk to fill in, a sheet of blank leaves to cut and write on, a gratitude jar label to collect notes instead. Every leaf and label is left blank for a family's or class's own words. Drawn rustic burlap-and-kraft, elegant gold-and-cream, or bright kids' style. Tape the filled leaves to the tree as the month goes on.",
        useCases: [
            'a classroom gratitude activity through November',
            'a Thanksgiving table centerpiece project',
            'a family tradition filled in daily',
            'a gratitude jar for notes read aloud at dinner',
        ],
        faq: [
            {q: 'Are the leaves already labeled?', a: "No — every leaf is left blank so each person writes their own thing they're thankful for."},
            {q: 'How do the leaves attach to the tree?', a: "Tape or a glue stick works to add each written leaf to the bare tree sheet as they're filled in."},
            {q: 'Can I change the size of the tree or the number of leaves?', a: "Yes — type a direction like 'a bigger tree' or 'fewer, larger leaves' and the sheets redraw."},
            {q: 'Is there a simpler version for young kids?', a: "The kids' set uses a bigger tree and fewer, larger leaves; the elegant and rustic sets carry more leaves for an older class or family."},
        ],
    },
}
